/**
 * OrbiterSystem — Manages shapes that orbit around a center point or entity.
 * Extracted from VFXEffectPlayer.
 *
 * Phase H — typed against substrate-agnostic IShape (instead of
 * Phaser.GameObjects.Shape). Mutations stay direct (.x / .y / .rotation)
 * since IShape exposes those as mutable.
 */

import type { VFXOrbitConfig } from '../VFXEffectRegistry';
import type { IShape } from '../../rendering/IRenderer';
import { entityVfxAnchor } from './entityVfxAnchor';

interface OrbiterData {
  radius: number;
  speed: number;
  angle: number;
  centerX: number;
  centerY: number;
  centerOffsetX: number;
  centerOffsetY: number;
  orient: boolean;
  followTarget?: string;
  cachedTarget?: any;
}

export class OrbiterSystem {
  private orbiters = new Map<IShape, OrbiterData>();

  /** Register a shape as an orbiter. */
  public add(
    obj: IShape,
    orbit: VFXOrbitConfig,
    centerX: number,
    centerY: number,
    followTarget?: string,
  ): void {
    this.orbiters.set(obj, {
      radius: orbit.radius,
      speed: orbit.speed,
      angle: orbit.startAngle || 0,
      centerX,
      centerY,
      centerOffsetX: orbit.centerOffsetX ?? 0,
      centerOffsetY: orbit.centerOffsetY ?? 0,
      orient: orbit.orientToPath || false,
      followTarget,
    });
  }

  /** Remove a shape from the orbiter system. */
  public remove(obj: IShape): void {
    this.orbiters.delete(obj);
  }

  /** Update all active orbiters. Call from VFXEffectPlayer.update(). */
  public update(dt: number, resolveTarget: (id: string) => any): void {
    this.orbiters.forEach((data, obj) => {
      if (!obj.active) {
        this.orbiters.delete(obj);
        return;
      }

      data.angle += data.speed * Math.PI * 2 * dt;

      let cx = data.centerX;
      let cy = data.centerY;

      if (data.followTarget) {
        let target = data.cachedTarget;
        if (!target || target.isDestroyed || !target.position) {
          target = resolveTarget(data.followTarget);
          data.cachedTarget = target;
        }
        const anchor = entityVfxAnchor(target);
        if (anchor) {
          cx = anchor.x;
          cy = anchor.y;
        }
      }

      obj.x = cx + data.centerOffsetX + Math.cos(data.angle) * data.radius;
      obj.y = cy + data.centerOffsetY + Math.sin(data.angle) * data.radius;

      if (data.orient) {
        obj.rotation = data.angle + Math.PI / 2;
      }
    });
  }

  public clear(): void {
    this.orbiters.clear();
  }
}
